/**
 * 🟦 V12.1 — Style Pack Diff Viewer.
 *
 * Muestra cambios token-por-token entre el theme actual y el del pack,
 * lista los effects que se aplican y las recipes que se aplican al root.
 * Read-only. Botones: "Aplicar pack" (delegado al padre) y "Cancelar".
 */
import { useMemo } from 'react';
import { X, Sparkles, ArrowRight, Check, Layers } from 'lucide-react';
import { BACKEND_URL } from '../../config/api';

const COLOR_TOKENS = ['primary', 'secondary', 'background', 'surface', 'text', 'border'];
const RADIUS_TOKENS = ['sm', 'md', 'lg'];

const ColorDiff = ({ token, current, next }) => {
  const changed = String(current || '').toLowerCase() !== String(next || '').toLowerCase();
  return (
    <div
      data-testid={`stylepack-diff-color-${token}`}
      className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded ${changed ? 'bg-amber-950/40 border border-amber-500/30' : 'bg-slate-900/40'}`}
    >
      <span className="text-slate-400 w-20 font-mono">{token}</span>
      <span className="w-5 h-5 rounded border border-slate-700" style={{ background: current || '#000' }} />
      <span className="font-mono text-slate-500 text-[10px]">{current || '—'}</span>
      <ArrowRight className="w-3 h-3 text-slate-500" />
      <span className="w-5 h-5 rounded border border-slate-700" style={{ background: next || '#000' }} />
      <span className={`font-mono text-[10px] ${changed ? 'text-amber-200' : 'text-slate-500'}`}>{next || '—'}</span>
      {changed && <span className="text-[9px] text-amber-300 ml-auto">CHANGED</span>}
    </div>
  );
};

const TokenDiff = ({ testid, label, current, next }) => {
  const changed = String(current || '') !== String(next || '');
  return (
    <div
      data-testid={testid}
      className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded ${changed ? 'bg-amber-950/40 border border-amber-500/30' : 'bg-slate-900/40'}`}
    >
      <span className="text-slate-400 w-20 font-mono">{label}</span>
      <span className="font-mono text-slate-500 text-[10px]">{current || '—'}</span>
      <ArrowRight className="w-3 h-3 text-slate-500" />
      <span className={`font-mono text-[10px] ${changed ? 'text-amber-200' : 'text-slate-500'}`}>{next || '—'}</span>
      {changed && <span className="text-[9px] text-amber-300 ml-auto">CHANGED</span>}
    </div>
  );
};

const LyonStylePackDiffViewer = ({ open, pack, currentTheme, onApply, onCancel, applying, es = true }) => {
  const diff = useMemo(() => {
    if (!pack) return { colors: [], radii: [], shadows: [], effects: [], recipesCount: 0 };
    const colors = COLOR_TOKENS.map((t) => ({
      token: t,
      current: currentTheme?.colors?.[t],
      next: pack.theme?.colors?.[t],
    }));
    const radii = RADIUS_TOKENS.map((r) => ({
      token: r,
      current: currentTheme?.radii?.[r],
      next: pack.theme?.radii?.[r],
    }));
    const shadowsKeys = new Set([...Object.keys(currentTheme?.shadows || {}), ...Object.keys(pack.theme?.shadows || {})]);
    const shadows = Array.from(shadowsKeys).map((k) => ({
      token: k,
      current: currentTheme?.shadows?.[k],
      next: pack.theme?.shadows?.[k],
    }));
    const effects = Object.entries(pack.effects || {}).map(([k, v]) => ({ key: k, value: v }));
    return { colors, radii, shadows, effects, recipesCount: (pack.recipes || []).length };
  }, [pack, currentTheme]);

  if (!open || !pack) return null;

  return (
    <div
      data-testid="stylepack-diff-viewer"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
      className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-slate-950 border border-amber-500/40 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-gradient-to-r from-amber-950/40 to-purple-950/30">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-300" />
            {es ? `Vista previa de cambios: ${pack.name}` : `Diff preview: ${pack.name}`}
            <span className="text-[10px] font-mono text-slate-400">v{pack.version || '1.0.0'}</span>
          </h3>
          <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Preview IA */}
          <div className="flex gap-3">
            <img
              src={`${BACKEND_URL}${pack.previewImageUrl || ''}`}
              alt={pack.name}
              loading="lazy"
              data-testid="stylepack-diff-preview"
              onError={(e) => { e.currentTarget.style.opacity = '0.4'; }}
              className="w-32 h-32 object-cover rounded-md bg-slate-800/60 border border-slate-700/40 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white">{pack.name}</p>
              <p className="text-[11px] text-slate-400 mt-1">{pack.description}</p>
              <p className="text-[10px] text-slate-500 mt-2 font-mono">
                {diff.recipesCount} recipes · {diff.effects.length} effects · {diff.colors.filter(c => String(c.current||'').toLowerCase() !== String(c.next||'').toLowerCase()).length} color changes
              </p>
            </div>
          </div>

          {/* Colors */}
          <section>
            <p className="text-[10px] uppercase text-slate-500 font-mono mb-1.5">Colors</p>
            <div className="space-y-1">
              {diff.colors.map((c) => <ColorDiff key={c.token} token={c.token} current={c.current} next={c.next} />)}
            </div>
          </section>

          {/* Radii */}
          <section>
            <p className="text-[10px] uppercase text-slate-500 font-mono mb-1.5">Radii</p>
            <div className="space-y-1" data-testid="stylepack-diff-radius">
              {diff.radii.map((r) => <TokenDiff key={`r-${r.token}`} testid={`stylepack-diff-radius-${r.token}`} label={r.token} current={r.current} next={r.next} />)}
            </div>
          </section>

          {/* Shadows */}
          {diff.shadows.length > 0 && (
            <section>
              <p className="text-[10px] uppercase text-slate-500 font-mono mb-1.5">Shadows</p>
              <div className="space-y-1" data-testid="stylepack-diff-shadow">
                {diff.shadows.map((s) => <TokenDiff key={`s-${s.token}`} testid={`stylepack-diff-shadow-${s.token}`} label={s.token} current={s.current} next={s.next} />)}
              </div>
            </section>
          )}

          {/* Effects */}
          {diff.effects.length > 0 && (
            <section>
              <p className="text-[10px] uppercase text-slate-500 font-mono mb-1.5">Effects</p>
              <div className="grid grid-cols-2 gap-1">
                {diff.effects.map((e) => (
                  <div
                    key={e.key}
                    data-testid={`stylepack-diff-effect-${e.key}`}
                    className="flex items-center gap-2 text-[11px] px-2 py-1 rounded bg-slate-900/40 border border-slate-700/40"
                  >
                    <span className="text-slate-400 font-mono">{e.key}</span>
                    <span className={`font-mono ml-auto ${e.value === true ? 'text-emerald-300' : e.value === false ? 'text-slate-500' : 'text-cyan-200'}`}>
                      {String(e.value)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Recipes */}
          {diff.recipesCount > 0 && (
            <section data-testid="stylepack-diff-recipes">
              <p className="text-[10px] uppercase text-slate-500 font-mono mb-1.5">Recipes ({diff.recipesCount})</p>
              <div className="space-y-1">
                {(pack.recipes || []).map((rid) => (
                  <div key={rid} className="flex items-center gap-2 text-[11px] px-2 py-1 rounded bg-slate-900/40 border border-slate-700/40">
                    <Layers className="w-3 h-3 text-fuchsia-300" />
                    <span className="font-mono text-fuchsia-200">{rid}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-800 flex items-center justify-end gap-2 bg-slate-950/80">
          <button
            type="button"
            onClick={onCancel}
            data-testid="stylepack-diff-cancel"
            className="px-3 py-1.5 rounded-md text-xs text-slate-300 hover:text-white hover:bg-slate-800"
          >
            {es ? 'Cancelar' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={applying}
            data-testid="stylepack-diff-apply"
            className="px-4 py-1.5 rounded-md text-xs font-bold bg-gradient-to-r from-amber-600 to-fuchsia-600 hover:from-amber-500 hover:to-fuchsia-500 text-white disabled:opacity-60 flex items-center gap-1"
          >
            <Check className="w-3.5 h-3.5" /> {applying ? (es ? 'Aplicando…' : 'Applying…') : (es ? 'Aplicar pack' : 'Apply pack')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LyonStylePackDiffViewer;
